









































































































































import { defineComponent, ref, computed, watch } from '@vue/composition-api';
import logic from '../logic';
import WorkbenchAccessore from './WorkbenchAccessore.vue';
import WorkbenchPreview from './WorkbenchPreview.vue';
import CustomSelect from './CustomSelect.vue';
import RadioGroup from './RadioGroup.vue';
import WeightRadioGroup from './WeightRadioGroup.vue';
import StandRadioGroup from './StandRadioGroup.vue';
import AnnotationMark from './AnnotationMark.vue';
import formatNumber from '../functions/formatNumber';
import arrayToggle from '../functions/arrayToggle';

interface Option {
	value: any,
	text: string,
	disabled?: boolean,
	title?: string,
}

export interface WorkbanchInterface {
	isDone: boolean;
	id: string | number;
	quantity: number;
	weight?: number | null;
	worktopLength?: number | null;
	worktopMaterial?: number | null;
	leftBase?: string | null;
	leftStand?: string | null;
	rightBase?: string | null;
	rightStand?: string | null;
	shelf?: string | null;
	bracket?: string | null;
	shield?: number | null;
	lighting?: number | null;
	accessores?: any[];
}

export default defineComponent({
	components: {
		WorkbenchAccessore,
		WorkbenchPreview,
		CustomSelect,
		RadioGroup,
		WeightRadioGroup,
		StandRadioGroup,
		AnnotationMark,
	},
	props: {
		id: {
			type: Number,
			required: true,
		},
		index: {
			type: Number,
			required: true,
		},
		isDone: {
			type: Boolean,
			required: true,
		},
		quantity: {
			type: Number,
			required: true,
		},
		price: {
			type: Number,
			required: true,
		},
		removable: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const step = ref(1);

		const weight = ref(null);
		const worktopLength = ref(null);
		const worktopMaterial = ref(null);
		const leftBase = ref('');
		const leftStand = ref<any>(null);
		const rightBase = ref('');
		const rightStand = ref<any>(null);
		const shield = ref(null);
		const lighting = ref(null);
		const accessores = ref<any[]>([]);
		const unawailableBaseCombinationText = 'Такая комбинация оснований недоступна для выбранной ширины столешницы. Попробуйте выбрать другую ширину на шаге 2 или другую комбинацию оснований'

		const workbench = computed(() => ({
			id: props.id,
			isDone: props.isDone,
			quantity: props.quantity,
			weight: weight.value,
			worktopLength: worktopLength.value,
			worktopMaterial: worktopMaterial.value,
			leftBase: leftBase.value,
			leftStand: leftStand.value,
			rightBase: rightBase.value,
			rightStand: rightStand.value,
			shelf: null,
			shield: shield.value,
			lighting: lighting.value,
			accessores: accessores.value,
		} as WorkbanchInterface));

		const shelf = computed(() => logic.hasShelf(workbench.value) && logic.getShelf(workbench.value) ? logic.getShelf(workbench.value).text : null);
		const bracket = computed(() => {
			const options = logic.getOptions(workbench.value);
			return logic.hasBracket(workbench.value) ? options.bracket.text : '';
		});
		const size = computed(() => logic.getSize(workbench.value));
		const maxWeight = computed(() => logic.getWeight(workbench.value));
		const baseValid = computed(() => {
			try {	
				return logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, leftBase.value, rightBase.value)	
			} catch (e) {	
				console.log('Error in baseValid computed: ', e.message)	
				return false	
			}	
		});
		const standValid = computed(() => logic.checkStand(leftStand.value, rightStand.value));
		const hasThirdStand = computed(() => logic.hasThirdStand(workbench.value));
		const totalSteps = computed(() => shield.value === 0 ? 4 : 5);
		const canDone = computed(() => step.value === totalSteps.value);
		const canPrev = computed(() => step.value > 1);
		const canNext = computed(() => {
			switch (step.value) {
				case 1:
					return weight.value !== null;
				case 2:
					return worktopLength.value !== null && worktopMaterial.value !== null;
				case 3:
					return leftBase.value && rightBase.value && baseValid.value && standValid.value;
				case 4:
					return shield.value !== null && lighting.value !== null;
			}
			return step.value < totalSteps.value;
		});

		const weightOptions = ref<Option[]>(logic.getWeightOptions());
		const worktopLengthOptions = computed<Option[]>(() => logic.getWorktopLengthOptions(weight.value));
		const worktopMaterialOptions = computed<Option[]>(() => logic.getWorktopMaterialOptions(weight.value, worktopLength.value));
		const baseOptions = ref<Option[]>(logic.getBaseOptions().map(opt => {
			return opt;
		}));
		const leftBaseOptions = computed<Option[]>(() => {
			const opts = JSON.parse(JSON.stringify(baseOptions.value))
			return opts.map(opt => {
				try {	
					if (rightBase.value) {
						opt.disabled = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, opt.value, rightBase.value)
					} else {
						const one = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, opt.value, 'S')
						const two = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, opt.value, 'T')
						opt.disabled = one && two;
					}
				} catch (e) {
					opt.disabled = true	
					console.log('Error in logic.checkBase: ', e.message)
				}
				opt.title = opt.disabled ? unawailableBaseCombinationText : ''
				return opt;	
			})	
		});	
		const rightBaseOptions = computed<Option[]>(() => {
			const opts = JSON.parse(JSON.stringify(baseOptions.value))
			return opts.map(opt => {
				try {
					if (leftBase.value) {
						opt.disabled = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, leftBase.value, opt.value)
					} else {
						const one = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, 'S', opt.value)
						const two = !logic.checkBase(weight.value, worktopLength.value, worktopMaterial.value, 'T', opt.value)
						opt.disabled = one && two;	
					}	
				} catch (e) {
					opt.disabled = true
					console.log('Error in logic.checkBase: ', e.message)
				}
				opt.title = opt.disabled ? unawailableBaseCombinationText : ''
				return opt;
			})
		});
		const leftStandOptions = computed<Option[]>(() => logic.getStandOptions(weight.value, worktopLength.value, leftBase.value));
		const rightStandOptions = computed<Option[]>(() => logic.getStandOptions(weight.value, worktopLength.value, rightBase.value));
		const shieldOptions = ref<Option[]>(logic.getShieldOptions());
		const lightingOptions = ref<Option[]>(logic.getLightingOptions());
		const accessoreOptions = ref<Option[]>(logic.getAccessoreOptions().map(item => ({ ...item, quantity: 1 })));
		const shieldText = computed(() => shieldOptions.value.find(option => option.value === shield.value)?.text);
		const lightningText = computed(() => lightingOptions.value.find(option => option.value === lighting.value)?.text);

		const standPlaceholder = {
			T: 'Выберите тумбу',
			S: 'Выберите опору',
		} as any;
		const leftStandPlaceholder = computed(() => standPlaceholder[leftBase.value]);
		const rightStandPlaceholder = computed(() => standPlaceholder[rightBase.value]);

		const prevStep = () => {
			step.value--;
		};
		const nextStep = () => {
			step.value++;
		};
		const remove = () => {
			emit('remove');
		};
		const update = () => {
			emit('update', {
				...workbench.value,
				shelf: shelf.value,
				bracket: bracket.value,
				size: size.value,
				maxWeight: maxWeight.value,
			});
		};
		const done = () => {
			emit('update', {
				...workbench.value,
				shelf: shelf.value,
				bracket: bracket.value,
				isDone: true,
				size: size.value,
				maxWeight: maxWeight.value,
			});
		};
		const edit = () => {
			emit('edit');
		};
		const toggleAccessore = (item: any) => {
			arrayToggle(accessores.value, item);
			update(); // workaround
		};

		watch(workbench, () => {
			update();
		});

		watch(shield, () => {
			if(shield!.value! !== null && shield!.value! < 1) {
				lighting.value = null;
			}
		});

		watch(step, () => {
			if (step.value <= 1) {
				worktopLength.value = null;
				worktopMaterial.value = null;
			}

			if (step.value <= 2) {
				leftBase.value = '';
				// leftStand.value = null;
				rightBase.value = '';
				// rightStand.value = null;
			}

			if (step.value <= 3) {
				shield.value = null;
			}

			if (step.value === 3) {
				setTimeout(() => {
					const activeLeft = leftBaseOptions.value.filter(v => !v.disabled)
					const activeRight = rightBaseOptions.value.filter(v => !v.disabled)
					if (activeLeft.length < 2) {
						leftBase.value = activeLeft[0].value;
					}
					if (activeRight.length < 2) {
						rightBase.value = activeRight[0].value;
					}
				}, 100)
			}

			if (step.value <= 4) {
				lighting.value = null;
			}

			if (step.value <= 5) {
				accessores.value = [];
			}
		});

		watch(worktopLength, () => {
			worktopMaterial.value = null;
		});

		watch(leftBase, () => {
			leftStand.value = leftStandOptions.value[0]?.text;
		});

		watch(rightBase, () => {
			rightStand.value = rightStandOptions.value[0]?.text;
		});

		const isPrice = computed(() => Boolean(props.price) && (step.value == 3 && canNext.value || step.value > 3));

		const workbenchClassObj = computed(() => {
			const obj = {'workbench_done': props.isDone}
			obj['step-' + step.value] = true

			return obj
		})

		return {
			workbench,
			step,
			totalSteps,
			size,
			maxWeight,
			weight,
			worktopLength,
			worktopMaterial,
			baseValid,
			standValid,
			hasThirdStand,
			leftBase,
			leftStandPlaceholder,
			leftStand,
			rightBase,
			rightStandPlaceholder,
			rightStand,
			shelf,
			bracket,
			shield,
			shieldText,
			lightningText,
			lighting,
			canDone,
			canPrev,
			canNext,
			isPrice,
			weightOptions,
			worktopLengthOptions,
			worktopMaterialOptions,
			baseOptions,
			leftBaseOptions,
			rightBaseOptions,
			unawailableBaseCombinationText,
			leftStandOptions,
			rightStandOptions,
			shieldOptions,
			lightingOptions,
			accessoreOptions,
			prevStep,
			nextStep,
			remove,
			update,
			done,
			edit,
			formatNumber,
			accessores,
			toggleAccessore,
			workbenchClassObj
		};
	},
})
